<script>
// @ts-check

import { getActiveDimensions, getDimensions } from '@/api/reportingDimensions'
import { getIncomeStatementReport } from '@/api/reports'
import DateRangeField from '@/components/fields/DateRangeField.vue'
import FileViewer from '@/components/file/FileViewer.vue'
import { demoMixin } from '@/core/mixins/demo'
import {
  formattedDimension,
  formattedPeriod,
  formattedTag,
  toCamelCase
} from '@/helpers/formatters'
import { downloadFile } from '@/helpers/functions'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'IncomeStatement',
  components: {
    FileViewer,
    DateRangeField
  },
  mixins: [demoMixin],
  data: () => ({
    accountingPeriodId: null,
    periodData: null,
    /** @type {?AttachmentResource} */
    report: null,
    isLoadingPeriods: false,
    isLoadingReport: false,
    startDate: null,
    endDate: null,
    dimension: null,
    /** @type {any[]} */
    dimensions: [],
    tag: null,
    /** @type {any[]} */
    tags: []
  }),
  computed: {
    ...mapGetters('reports', ['accountingPeriods']),
    ...mapGetters('mobileData', ['isMobileWrapperUser']),
    ...mapGetters('app', ['isOyService']),
    formattedAccountingPeriodOptions() {
      return this.accountingPeriods.map(formattedPeriod)
    },
    formattedDimensions() {
      return this.dimensions.map(formattedDimension)
    },
    formattedTags() {
      if (!this.dimension) {
        return []
      }

      return [
        ...this.tags.data
          .filter(({ dimensionType }) => dimensionType === this.dimension)
          .map(formattedTag)
      ]
    },
    dateRange: {
      get() {
        return [this.startDate, this.endDate]
      },
      set([startDate, endDate]) {
        this.startDate = startDate
        this.endDate = endDate
      }
    },
    refetchWatcher() {
      return {
        tag: this.tag,
        dimension: this.dimension,
        dateRange: this.dateRange
      }
    }
  },
  watch: {
    accountingPeriodId(newPeriod) {
      if (!newPeriod) {
        return
      }

      this.periodData = this.accountingPeriods.find(
        ({ id }) => `${id}` === `${this.accountingPeriodId}`
      )

      this.dateRange = [
        this.periodData.period_start,
        this.periodData.period_end
      ]
    },
    async refetchWatcher() {
      if (this.startDate && this.endDate) {
        await this.skipInDemoMode(this.getIncomeStatementReport)
      } else {
        this.report = null
      }

      if (
        this.periodData &&
        (this.periodData.period_start !== this.startDate ||
          this.periodData.period_end !== this.endDate)
      ) {
        this.accountingPeriodId = null
        this.periodData = null
      }
    }
  },
  async created() {
    this.isLoadingPeriods = true

    await this.skipInDemoMode(this.fetchAccountingPeriods)

    if (this.isOyService) {
      ;[this.dimensions, this.tags] = toCamelCase(
        await Promise.all([this.fetchDimensions(), this.fetchTags()])
      )
    }

    this.isLoadingPeriods = false
  },
  methods: {
    ...mapActions('reports', ['getAccountingPeriods']),
    async fetchDimensions() {
      return getActiveDimensions()
    },
    async fetchTags() {
      return getDimensions()
    },
    async fetchAccountingPeriods() {
      try {
        await this.getAccountingPeriods()

        const accountingPeriod = this.accountingPeriods.find(
          ({ current }) => current === true
        )

        if (accountingPeriod) {
          this.accountingPeriodId = accountingPeriod.id
        }
      } catch (error) {
        console.error(error)
      }
    },
    downloadReport() {
      if (!this.report) {
        return
      }

      downloadFile(
        this.report.file,
        `${this.startDate.replace(/-/g, '')}_income_statement.pdf`,
        'application/pdf',
        this.isMobileWrapperUser
      )
    },
    async getIncomeStatementReport() {
      this.report = null
      this.isLoadingReport = true

      try {
        this.report = await getIncomeStatementReport(
          this.startDate,
          this.endDate,
          null,
          true,
          this.dimension,
          this.tag
        )
      } catch (error) {
        console.error(error)
      }

      this.isLoadingReport = false
    }
  }
}
</script>

<template>
  <div v-if="isLoadingPeriods" class="my-4 flex items-center justify-center">
    <p>{{ $t('reports.loading_account_periods') }}</p>
    <ProgressCircular class="ml-2" />
  </div>

  <div v-else>
    <p class="mb-4">
      {{ $t('reports.only_shows_accepted_entries') }}
    </p>

    <div class="grid-controls xl:grid-cols-2">
      <VSelect
        v-model="accountingPeriodId"
        :label="$t('form.label.accounting_period')"
        :placeholder="$t('reports.choose_accounting_period')"
        :items="formattedAccountingPeriodOptions"
        item-title="label"
        item-value="value"
      />
      <DateRangeField
        v-model="dateRange"
        :label="$t('reports.get_daterange')"
      />

      <template v-if="isOyService">
        <VAutocomplete
          v-model="dimension"
          :label="$t('reporting_dimensions.form.dimension')"
          :placeholder="$t('reports.choose_dimension')"
          item-title="label"
          clearable
          :items="formattedDimensions"
        />
        <VAutocomplete
          v-model="tag"
          :label="$t('reporting_dimensions.form.tag')"
          :placeholder="$t('reports.choose_tag')"
          item-title="label"
          clearable
          :items="formattedTags"
        />
      </template>
    </div>

    <FileViewer
      v-if="report && !isInDemoMode"
      class="my-4 w-full"
      :file="report"
    />

    <div
      v-else-if="isLoadingReport"
      class="my-8 flex items-center justify-center"
    >
      <p>{{ $t('reports.loading_income_statement') }}</p>
      <ProgressCircular class="ml-2" />
    </div>

    <p v-else-if="!isInDemoMode" class="mt-2 flex items-center justify-center">
      {{ $t('reports.select_income_statement_date_range') }}
    </p>

    <div v-if="!isInDemoMode" class="mt-2 justify-end sm:flex">
      <PrimaryButton
        v-show="report"
        class="w-full lg:w-auto"
        :text="$t('reports.download_income_statement')"
        @click="downloadReport"
      />
    </div>
  </div>
</template>
